import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  getAllDrinks,
  getSingleDrink,
  createDrink,
  updateDrink,
  deleteDrink,
  websiteOrderingAvailableItemsToggle
} from '@/api/new-api/staff-management/menu-manage/drink'
import { deleteGiftCardApi, getAllGiftCardApi } from '@/api/giftCard'
import { sendGiftCardMail } from '@/api/new-api/loyalty/gift-card'

// Thunks

export const fetchAllGiftCards = createAsyncThunk(
  'giftCards/fetchAll',
  async (
    { restaurant_id, page = 1, search = '', status = '', per_page = 10 },
    { rejectWithValue }
  ) => {
    const params = new URLSearchParams()

    params.append('per_page', per_page)
    params.append('page', page)

    if (search) {
      params.append('search_term', search)
    }

    if (status) {
      params.append('status', status)
    }

    const res = await getAllGiftCardApi(restaurant_id, params.toString())

    if (res.status === 'success') {
      return { ...res.data, search }
    }

    return rejectWithValue(res.message)
  }
)

export const fetchSingleGiftCard = createAsyncThunk(
  'giftCards/fetchOne',
  async (id, { rejectWithValue }) => {
    const res = await getSingleDrink(id)
    if (res.status === 'success') return res.data.drink
    return rejectWithValue(res.message)
  }
)

export const createNewGiftCard = createAsyncThunk(
  'giftCards/create',
  async (data, { rejectWithValue }) => {
    const res = await createDrink(data)
    if (res.status === 'success') return res.data.drink
    return rejectWithValue(res.message)
  }
)

export const updateExistingGiftCard = createAsyncThunk(
  'giftCards/update',
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateDrink(id, data)
    if (res.status === 'success') return res.data.drink
    return rejectWithValue(res.message)
  }
)

export const deleteGiftById = createAsyncThunk(
  'giftCards/delete',
  async (id, { rejectWithValue }) => {
    const res = await deleteGiftCardApi(id)
    if (res.status === 'success') return id
    return rejectWithValue(res.message)
  }
)

export const sendGiftCardEmail = createAsyncThunk(
  'giftCards/sendEmail',
  async ({ id, data, onProgress, signal }, { rejectWithValue }) => {
    const config = {};
    if (signal) config.signal = signal;
    if (onProgress) {
      config.onDownloadProgress = (progressEvent) => {
        const loaded = progressEvent.loaded || 0;
        const total = progressEvent.total || null;
        let percent = null;
        if (total && total > 0) {
          percent = Math.round((loaded * 100) / total);
        }
        onProgress({ loaded, total, percent });
      };
    }
    const res = await sendGiftCardMail(id, data, config);
    if (res.status === 'success') return res.data;
    return rejectWithValue(res.message);
  }
)

// Initial State

const initialState = {
  giftCards: [],
  singleGiftCard: null,
  totalPages: 1,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
  search: '',
  status: '',
  perPage: 10,
  currentPage: 1
}

// Slice

const giftCardSlice = createSlice({
  name: 'giftCards',
  initialState,
  reducers: {
    clearGiftCardMessages: state => {
      state.success = null
      state.error = null
      state.requestStatus = false
      state.totalPages = 1
      state.search = ''
    },
    setGiftCardSearch: (state, action) => {
      state.search = action.payload
    },
    setStatus: (state, action) => {
      state.status = action.payload
    },
    setPerPage: (state, action) => {
      state.perPage = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    }
  },
  extraReducers: builder => {
    builder

      // Fetch All
      .addCase(fetchAllGiftCards.pending, state => {
        state.getLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(fetchAllGiftCards.fulfilled, (state, action) => {
        state.getLoading = false
        state.giftCards = action.payload.gift_cards
        state.requestStatus = true
        state.totalPages = action.payload.pagination.last_page
      })
      .addCase(fetchAllGiftCards.rejected, (state, action) => {
        state.getLoading = false
        state.error = action.payload
        state.requestStatus = false
      })

      // Fetch Single
      .addCase(fetchSingleGiftCard.pending, state => {
        state.getLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(fetchSingleGiftCard.fulfilled, (state, action) => {
        state.getLoading = false
        state.singleDrink = action.payload
        state.requestStatus = true
      })
      .addCase(fetchSingleGiftCard.rejected, (state, action) => {
        state.getLoading = false
        state.error = action.payload
        state.requestStatus = false
      })

      // Create
      .addCase(createNewGiftCard.pending, state => {
        state.createLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(createNewGiftCard.fulfilled, (state, action) => {
        state.createLoading = false
        state.drinks.push(action.payload)
        state.success = 'Drink created successfully!'
        state.requestStatus = true
      })
      .addCase(createNewGiftCard.rejected, (state, action) => {
        state.createLoading = false
        state.error = action.payload
        state.requestStatus = false
      })

      // Update
      .addCase(updateExistingGiftCard.pending, state => {
        state.updateLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(updateExistingGiftCard.fulfilled, (state, action) => {
        state.updateLoading = false
        state.drinks = state.drinks.map(drink =>
          drink.id === action.payload.id ? action.payload : drink
        )
        state.success = 'Drink updated successfully!'
        state.requestStatus = true
      })
      .addCase(updateExistingGiftCard.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
        state.requestStatus = false
      })

      // Delete
      .addCase(deleteGiftById.pending, state => {
        state.deleteLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(deleteGiftById.fulfilled, (state, action) => {
        state.deleteLoading = false
        state.giftCards = state.giftCards.filter(
          item => item.id !== action.payload
        )
        state.success = 'Gift Card deleted successfully!'
        state.requestStatus = true
      })
      .addCase(deleteGiftById.rejected, (state, action) => {
        state.deleteLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
      // Send Email
      .addCase(sendGiftCardEmail.pending, state => {
        state.updateLoading = true
        state.error = null
        state.requestStatus = false
      })
      .addCase(sendGiftCardEmail.fulfilled, (state, action) => {
        state.updateLoading = false
        state.success = 'Gift Card email sent successfully!'
        state.requestStatus = true
      })
      .addCase(sendGiftCardEmail.rejected, (state, action) => {
        state.updateLoading = false
        state.error = action.payload
        state.requestStatus = false
      })
  }
})

export const {
  clearGiftCardMessages,
  setGiftCardSearch,
  setStatus,
  setPerPage,
  setCurrentPage
} = giftCardSlice.actions
export default giftCardSlice.reducer
